import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import { onSubmit } from "./action";
import "./login.scss";

const Logo = "https://newshop.in/images/logo.png";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="login-page">
      {/* Aurora mesh */}
      <div className="login-page__aurora" />
      <div className="login-page__blob" />

      {/* Dot-grid background */}
      <div className="login-page__grid" />

      {/* ── Left panel — branding ── */}
      <div className="login-page__left">
        {/* Corner accents */}
        <div className="login-page__corner login-page__corner--tl" />
        <div className="login-page__corner login-page__corner--bl" />

        {/* Globe rings */}
        <div className="login-page__ring-wrap">
          <div className="login-page__ring login-page__ring--1" />
          <div className="login-page__ring login-page__ring--2" />
          <div className="login-page__ring login-page__ring--3" />
          <div className="login-page__ring login-page__ring--4" />
        </div>

        <div className="login-page__brand">
          <div className="login-page__logo-wrap">
            <img src={Logo} alt="The New Shop" />
          </div>

          <h1 className="login-page__headline">
            <span className="login-page__headline--line">Retail</span>
            <span className="login-page__headline--line">Intelligence</span>
            <span className="login-page__headline--line">Platform</span>
          </h1>

          <p className="login-page__sub">
            Manage inventory, orders, transfers<br />
            and analytics — all in one place.
          </p>

          <div className="login-page__stats">
            <div className="login-page__stat">
              <span className="login-page__stat-num">500+</span>
              <span className="login-page__stat-label">Stores</span>
            </div>
            <div className="login-page__stat">
              <span className="login-page__stat-num">1M+</span>
              <span className="login-page__stat-label">Orders / month</span>
            </div>
            <div className="login-page__stat">
              <span className="login-page__stat-num">99.9%</span>
              <span className="login-page__stat-label">Uptime</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div className="login-page__right">
        {/* Corner accents */}
        <div className="login-page__corner login-page__corner--tr" />
        <div className="login-page__corner login-page__corner--br" />

        <div className="login-page__form-card">
          <div className="login-page__form-header">
            <h2 className="login-page__form-title">Welcome back</h2>
            <p className="login-page__form-sub">Sign in to your dashboard account</p>
          </div>

          <Formik
            initialValues={{ email: "", password: "" }}
            validate={(values) => {
              const errors = {};
              if (!values.email)    errors.email    = "Username is required";
              if (!values.password) errors.password = "Password is required";
              return errors;
            }}
            onSubmit={(values) => {
              dispatch(onSubmit(values, navigate));
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
              <Form onSubmit={handleSubmit} className="login-page__form">

                {/* Username */}
                <div className="login-field">
                  <label className="login-field__label">Username</label>
                  <div className="login-field__wrap">
                    <span className="login-field__icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      className={`login-field__input${errors.email && touched.email ? " login-field__input--error" : ""}`}
                      type="text"
                      name="email"
                      placeholder="Enter your username"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="username"
                    />
                  </div>
                  {errors.email && touched.email && (
                    <span className="login-field__error">{errors.email}</span>
                  )}
                </div>

                {/* Password */}
                <div className="login-field">
                  <label className="login-field__label">Password</label>
                  <div className="login-field__wrap">
                    <span className="login-field__icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1.5" fill="currentColor" />
                      </svg>
                    </span>
                    <input
                      className={`login-field__input${errors.password && touched.password ? " login-field__input--error" : ""}`}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter your password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="login-field__toggle"
                      onClick={() => setShowPassword((p) => !p)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M3 3l18 18M10.5 10.7A3 3 0 0 0 13.3 13.5M6.5 6.6C4.6 7.9 3 10 2 12c2 4.4 6 7 10 7 1.8 0 3.5-.5 5-1.4M9 5.1A10 10 0 0 1 12 5c4 0 8 2.6 10 7-1 2-2.6 3.9-4.5 5.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M2 12c2-4.4 6-7 10-7s8 2.6 10 7c-2 4.4-6 7-10 7S4 16.4 2 12z" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <span className="login-field__error">{errors.password}</span>
                  )}
                </div>

                <button type="submit" className="login-page__submit">
                  <span>Sign In</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </Form>
            )}
          </Formik>

          <p className="login-page__footer">
            © {new Date().getFullYear()} The New Shop · All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
